import { useEffect, useState } from "react";
import { NavLink, Navigate, Outlet } from "react-router-dom";
import { motion, useReducedMotion } from "framer-motion";
import { CalendarClock, Home, Lock, LogOut, Search, ShieldAlert, ShoppingBag, UserPlus, Wallet, Wrench, LineChart } from "lucide-react";
import { DESK } from "../../constants/testIds";
import { useDeskData } from "../../contexts/DeskDataContext";
import { useOnlineStatus } from "../../hooks/useOnlineStatus";
import { OfflineBanner } from "../common/ErrorState";
import { GymLoader } from "../common/GymLoader";
import { fadeIn } from "../../lib/motion";
import { TrialBlockModal } from "../trial/TrialBlockModal";
import { GlobalSearch } from "../shell/GlobalSearch";
import { EmulationFlag } from "../emulation/EmulationFlag";
import { OnboardingTourOverlay, OnboardingHelpButton } from "../onboarding/OnboardingTour";
import { useOnboardingTour } from "../onboarding/useOnboardingTour";
import { DESK_STEPS } from "../onboarding/onboardingSteps";

const NAV_ITEMS = [
  { key: "today", label: "Today", path: "/desk/today", icon: Home },
  { key: "shift", label: "Shift", path: "/desk/shift", icon: CalendarClock },
  { key: "members", label: "Members", path: "/desk/members", icon: Search },
  { key: "checkin", label: "Check-in", path: "/desk/checkin", icon: ShieldAlert },
  { key: "money", label: "Money In", path: "/desk/money", icon: Wallet },
  { key: "retail", label: "Retail", path: "/desk/retail", icon: ShoppingBag, module: "retail" },
  { key: "leads", label: "Leads", path: "/desk/leads", icon: UserPlus },
  { key: "floorops", label: "Floor Ops", path: "/desk/floor-ops", icon: Wrench },
  // UX-FD-03 §1 highest-risk rule #1: this whole section must be genuinely ABSENT (not
  // disabled) for an FD-tier login — gated below on `role`, same treatment as the `module` gate.
  { key: "mgr", label: "Manager", path: "/desk/mgr", icon: LineChart, role: "front_of_house_mgr" },
];

// UX-FD-01 §0 "a workhorse tool, not a showcase": deliberately a SLIM solid bar + flat
// horizontal tabs — no glass/blur, no video background, none of the Owner Cockpit's ceremony.
// Front Desk staff live in this screen all shift on a tablet at a counter; every extra pixel of
// chrome is pixels not spent on the next member's name. UX-FD-02 §1: Retail's nav item is
// filtered out entirely (not rendered, not disabled) when `entitlements.retail` is falsy —
// genuine module-gating, matching the checklist's "absent, not just disabled" requirement.
export function DeskShell() {
  const { me, branches, branchId, entitlements, readOnlyLapse, loading, error, logout } = useDeskData();
  const isOnline = useOnlineStatus();
  const reducedMotion = useReducedMotion();
  const [capModal, setCapModal] = useState({ open: false, message: null });
  const tour = useOnboardingTour("desk", me?.login_count, me?.id);

  useEffect(() => {
    const handler = (e) => setCapModal({ open: true, message: e.detail?.message });
    window.addEventListener("fitfrik:trial-cap-exceeded", handler);
    return () => window.removeEventListener("fitfrik:trial-cap-exceeded", handler);
  }, []);

  if (loading) return <GymLoader fullPage label="Loading Front Desk…" testId="desk-shell-loading" />;
  if (error) return <Navigate to="/login" replace />;

  const branch = branches.find((b) => b.id === branchId);
  const navItems = NAV_ITEMS.filter((item) => (!item.module || entitlements[item.module]) && (!item.role || me?.role === item.role));

  return (
    <div data-testid={DESK.shell} style={{ background: "#F4F3EF", minHeight: "100vh", paddingTop: me?.emulation?.active ? "var(--emulation-flag-height)" : undefined }}>
      {/* UX-CP-03: was previously mounted ONLY in the Owner Cockpit's AppShell - a Kairoo
          operator emulating Front Desk/Manager got no ribbon, no timer, no Escalate/End-session
          controls anywhere in this entire shell. `me` here already carries the same `emulation`
          shape (this context also calls `/me`) - just nobody rendered it. */}
      <EmulationFlag emulation={me?.emulation} orgId={me?.organization_id} gymName={me?.organization_display_name} />
      <div className="flex items-center justify-between gap-4 px-5 py-3" style={{ background: "#141416", color: "#fff" }}>
        <div className="flex items-center gap-3 shrink-0">
          <span className="font-bold text-sm tracking-tight">FitFrikArena</span>
          <span className="opacity-40">·</span>
          <span data-testid={DESK.branchName} className="text-sm font-medium">{branch?.name || "Front Desk"}</span>
        </div>
        <div className="flex-1 max-w-sm">
          <GlobalSearch basePath="/desk/members" testId={DESK.globalSearchInput} variant="dark" />
        </div>
        <div className="flex items-center gap-4 shrink-0">
          <span className="text-sm opacity-70">{me?.name}</span>
          <button
            data-testid={DESK.logoutButton}
            onClick={logout}
            className="flex items-center gap-1.5 text-sm font-medium opacity-80 hover:opacity-100 transition-opacity scarlet-focusable"
          >
            <LogOut size={14} /> Log out
          </button>
        </div>
      </div>

      <div className="flex items-center gap-1 px-4 overflow-x-auto" style={{ background: "#1E1E22", borderBottom: "1px solid rgba(255,255,255,0.06)" }}>
        {navItems.map((item) => (
          <NavLink
            key={item.key}
            to={item.path}
            data-testid={DESK.navItem(item.key)}
            className={({ isActive }) =>
              `flex items-center gap-1.5 px-4 py-3 text-sm font-semibold border-b-2 transition-colors shrink-0 ${isActive ? "border-[#D6202B] text-white" : "border-transparent text-white/50 hover:text-white/80"}`
            }
          >
            <item.icon size={14} /> {item.label}
          </NavLink>
        ))}
      </div>

      <main className="p-5 max-w-5xl mx-auto">
        {!isOnline && <OfflineBanner lastUpdatedLabel="last sync" />}
        {readOnlyLapse && (
          <motion.div
            data-testid={DESK.readOnlyBanner}
            {...fadeIn(reducedMotion)}
            className="mb-4 rounded-[10px] px-4 py-3 flex items-center gap-2 text-sm font-medium"
            style={{ background: "var(--status-overdue-bg)", color: "var(--status-overdue-text)" }}
          >
            <Lock size={16} />
            This organization's license has lapsed — new sales/expenses are blocked until billing is resolved. You can still view everything.
          </motion.div>
        )}
        <motion.div {...fadeIn(reducedMotion)}>
          <Outlet />
        </motion.div>
      </main>

      <TrialBlockModal open={capModal.open} onOpenChange={(v) => setCapModal((c) => ({ ...c, open: v }))} message={capModal.message} />
      {!me?.emulation?.active && (
        <>
          <OnboardingTourOverlay steps={DESK_STEPS} tour={tour} />
          <OnboardingHelpButton onClick={tour.restart} />
        </>
      )}
    </div>
  );
}
